import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  Fade,
  Slide,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  CircularProgress,
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  Lock,
  Visibility,
  VisibilityOff,
  ArrowBack,
  Security,
  CheckCircle,
  Send,
  LockReset,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useEffect } from 'react';
import { userService } from '../../services/apiServices';

// Step validation schemas
const usernameValidationSchema = yup.object({
  userName: yup
    .string('Enter your username')
    .required('Username is required'),
  contactMethod: yup
    .string('Select contact method')
    .oneOf(['email', 'phone'], 'Please select email or phone')
    .required('Contact method is required'),
  contactValue: yup
    .string('Enter contact details')
    .when('contactMethod', {
      is: 'email',
      then: (schema) => schema.email('Enter a valid email').required('Email is required'),
      otherwise: (schema) => schema.matches(/^[0-9]{10}$/, 'Phone number must be 10 digits').required('Phone number is required'),
    }),
});

const otpValidationSchema = yup.object({
  otp: yup
    .string('Enter OTP')
    .matches(/^[0-9]{6}$/, 'OTP must be 6 digits')
    .required('OTP is required'),
});

const passwordValidationSchema = yup.object({
  newPassword: yup
    .string('Enter your new password')
    .min(6, 'Password should be of minimum 6 characters length')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    )
    .required('New password is required'),
  confirmPassword: yup
    .string('Confirm your password')
    .oneOf([yup.ref('newPassword'), null], 'Passwords must match')
    .required('Confirm password is required'),
});

const steps = ['Verify Identity', 'Enter OTP', 'Reset Password'];

const ForgotPassword = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [userInfo, setUserInfo] = useState(null);

  // Step 1: Username and contact verification
  const usernameFormik = useFormik({
    initialValues: {
      userName: '',
      contactMethod: 'email',
      contactValue: '',
    },
    validationSchema: usernameValidationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError('');
      try {
        // API call to verify username and send OTP
        const response = await userService.verifyUserForReset(values);

        if (response.data.success) {
          setUserInfo(response.data.user);
          setOtpSent(true);
          setSuccess(`OTP sent to your ${values.contactMethod}`);
          setActiveStep(1);
          startResendTimer();
        } else {
          setError(response.data.message || 'Failed to verify username');
        }
      } catch (err) {
        console.error('Verify username error:', err);
        const errorMessage = err.response?.data?.message || 'Network error. Please try again.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  // Step 2: OTP verification
  const otpFormik = useFormik({
    initialValues: {
      otp: '',
    },
    validationSchema: otpValidationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError('');
      try {
        // API call to verify OTP
        const otpData = {
          userName: usernameFormik.values.userName,
          otp: values.otp,
          contactMethod: usernameFormik.values.contactMethod,
        };
        
        const response = await userService.verifyOTP(otpData);

        if (response.data.success) {
          setSuccess('OTP verified successfully!');
          setActiveStep(2);
        } else {
          setError(response.data.message || 'Invalid OTP');
        }
      } catch (err) {
        console.error('OTP verification error:', err);
        const errorMessage = err.response?.data?.message || 'Network error. Please try again.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  // Step 3: Password reset
  const passwordFormik = useFormik({
    initialValues: {
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: passwordValidationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError('');
      try {
        // API call to reset password
        const resetData = {
          userName: usernameFormik.values.userName,
          newPassword: values.newPassword,
          otp: otpFormik.values.otp,
        };
        
        const response = await userService.resetPassword(resetData);

        if (response.data.success) {
          setSuccess('Password reset successfully!');
          setTimeout(() => {
            navigate('/auth/login', { 
              state: { message: 'Password reset successful. Please login with your new password.' }
            });
          }, 2000);
        } else {
          setError(response.data.message || 'Failed to reset password');
        }
      } catch (err) {
        console.error('Password reset error:', err);
        const errorMessage = err.response?.data?.message || 'Network error. Please try again.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  // Timer for resend OTP
  const startResendTimer = () => {
    setResendTimer(60);
    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Resend OTP
  const resendOtp = async () => {
    if (resendTimer > 0) return;
    
    setLoading(true);
    setError('');
    try {
      const resendData = {
        userName: usernameFormik.values.userName,
        contactMethod: usernameFormik.values.contactMethod,
        contactValue: usernameFormik.values.contactValue,
      };
      
      const response = await userService.resendOTP(resendData);

      if (response.data.success) {
        setSuccess('OTP resent successfully!');
        startResendTimer();
      } else {
        setError(response.data.message || 'Failed to resend OTP');
      }
    } catch (err) {
      console.error('Resend OTP error:', err);
      const errorMessage = err.response?.data?.message || 'Network error. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const goBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
      setError('');
      setSuccess('');
    } else {
      navigate('/auth/login');
    }
  };

  // Ensure page is centered on load
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
    
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 50);

    return () => {
      clearTimeout(timer);
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  // Clear messages when step changes
  useEffect(() => {
    setError('');
    setSuccess('');
  }, [activeStep]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        height: 'auto',
        display: 'flex',
        position: 'relative',
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(135deg, #0a0e27 0%, #1a237e 50%, #3949ab 100%)'
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        backgroundAttachment: 'fixed',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="7" cy="7" r="2"/%3E%3Ccircle cx="37" cy="7" r="2"/%3E%3Ccircle cx="7" cy="37" r="2"/%3E%3Ccircle cx="37" cy="37" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        },
        overflow: 'hidden', // Prevent white screen issues
      }}
    >
      <Container 
        component="main" 
        maxWidth="xl"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          py: { xs: 2, sm: 3, lg: 4 },
          position: 'relative',
          zIndex: 1,
          px: { xs: 2, sm: 3, lg: 4 },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { 
              xs: '1fr', 
              md: '1fr', 
              lg: '1fr 1fr',
              xl: '1.2fr 1fr'
            },
            gap: { xs: 0, lg: 8, xl: 10 },
            width: '100%',
            maxWidth: { xs: '500px', lg: '1200px', xl: '1400px' },
            alignItems: 'center',
            mx: 'auto',
          }}
        >
          {/* Left Side - Enterprise Branding */}
          <Fade in timeout={1000}>
            <Box
              sx={{
                display: { xs: 'none', lg: 'flex' },
                flexDirection: 'column',
                justifyContent: 'center',
                color: 'white',
                pr: { lg: 4, xl: 6 },
                minHeight: { lg: '600px' },
              }}
            >
              <Box sx={{ mb: 6 }}>
                <Typography
                  variant="h2"
                  component="h1"
                  fontWeight="bold"
                  sx={{
                    fontSize: { lg: '2.8rem', xl: '3.5rem' },
                    mb: 2,
                    background: 'linear-gradient(45deg, #ffffff 30%, #e3f2fd 90%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    lineHeight: 1.2,
                  }}
                >
                  Secure Account Recovery
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 4,
                    opacity: 0.9,
                    fontWeight: 300,
                    lineHeight: 1.6,
                    fontSize: { lg: '1.2rem', xl: '1.4rem' },
                  }}
                >
                  Regain access to your IPL Fantasy account with our enterprise-grade security system
                </Typography>
              </Box>

              {/* Enterprise Feature Cards */}
              <Box sx={{ space: 'y-3' }}>
                <Card
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    mb: 3,
                    borderRadius: 3,
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                    <Security sx={{ color: '#4fc3f7', mr: 3, fontSize: 32 }} />
                    <Box>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
                        Enterprise Security
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.5 }}>
                        Multi-factor authentication with time-based OTP verification
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>

                <Card
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    mb: 3,
                    borderRadius: 3,
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                    <CheckCircle sx={{ color: '#66bb6a', mr: 3, fontSize: 32 }} />
                    <Box>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
                        Instant Verification
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.5 }}>
                        Secure codes delivered instantly via email or SMS
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>

                <Card
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 3,
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                    <LockReset sx={{ color: '#ff7043', mr: 3, fontSize: 32 }} />
                    <Box>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
                        Secure Reset Process
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.5 }}>
                        Industry-standard encryption for password reset workflow
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </Fade>

          {/* Right Side - Reset Form */}
          <Slide direction="left" in timeout={800}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <Paper
                elevation={24}
                sx={{
                  p: { xs: 4, sm: 5, lg: 6 },
                  borderRadius: 4,
                  backdropFilter: 'blur(30px)',
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.1)' 
                    : 'rgba(255, 255, 255, 0.98)',
                  border: theme.palette.mode === 'dark'
                    ? '1px solid rgba(255, 255, 255, 0.15)'
                    : '1px solid rgba(255, 255, 255, 0.4)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 25px 50px rgba(0, 0, 0, 0.5)'
                    : '0 25px 50px rgba(0, 0, 0, 0.15)',
                  transition: 'all 0.3s ease-in-out',
                  width: '100%',
                  maxWidth: { xs: '450px', sm: '500px', lg: '550px' },
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 30px 60px rgba(0, 0, 0, 0.6)'
                      : '0 30px 60px rgba(0, 0, 0, 0.2)',
                  },
                }}
              >
                {/* Mobile Header for Small Screens */}
                <Box sx={{ display: { xs: 'block', lg: 'none' }, textAlign: 'center', mb: 4 }}>
                  <Typography
                    variant="h4"
                    component="h1"
                    fontWeight="bold"
                    sx={{
                      mb: 1,
                      background: 'linear-gradient(45deg, #1976d2 30%, #64b5f6 90%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    Account Recovery
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Secure password reset with enterprise-grade security
                  </Typography>
                </Box>

                {/* Header */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                  p: 2,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  boxShadow: '0 8px 24px rgba(25, 118, 210, 0.3)',
                }}
              >
                <LockReset sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography
                component="h1"
                variant="h4"
                fontWeight="bold"
                sx={{
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(45deg, #90caf9 30%, #1976d2 90%)'
                    : 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                Reset Password
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: 400, mx: 'auto', lineHeight: 1.6 }}
              >
                Follow the steps below to reset your password securely
              </Typography>
            </Box>

            {/* Enhanced Stepper */}
            <Stepper 
              activeStep={activeStep} 
              alternativeLabel
              sx={{ 
                mb: 4,
                '& .MuiStepLabel-root .Mui-completed': {
                  color: 'success.main',
                },
                '& .MuiStepLabel-root .Mui-active': {
                  color: 'primary.main',
                },
                '& .MuiStepConnector-alternativeLabel': {
                  top: 10,
                  left: 'calc(-50% + 16px)',
                  right: 'calc(50% + 16px)',
                },
                '& .MuiStepConnector-active': {
                  '& .MuiStepConnector-line': {
                    borderColor: 'primary.main',
                  },
                },
                '& .MuiStepConnector-completed': {
                  '& .MuiStepConnector-line': {
                    borderColor: 'success.main',
                  },
                },
              }}
            >
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel 
                    sx={{
                      '& .MuiStepLabel-label': {
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        fontWeight: activeStep === index ? 600 : 400,
                      },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Back Button */}
            <Box sx={{ mb: 3 }}>
              <Button
                startIcon={<ArrowBack />}
                onClick={goBack}
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {activeStep === 0 ? 'Back to Login' : 'Previous Step'}
              </Button>
            </Box>

            {/* Error/Success Messages */}
            {error && (
              <Slide direction="down" in={!!error}>
                <Alert 
                  severity="error" 
                  sx={{ 
                    width: '100%', 
                    mb: 3,
                    borderRadius: 2,
                  }}
                >
                  {error}
                </Alert>
              </Slide>
            )}

            {success && (
              <Slide direction="down" in={!!success}>
                <Alert 
                  severity="success" 
                  sx={{ 
                    width: '100%', 
                    mb: 3,
                    borderRadius: 2,
                  }}
                >
                  {success}
                </Alert>
              </Slide>
            )}

                          {/* Back Button for Mobile */}
              {activeStep > 0 && (
                <Box sx={{ display: { xs: 'flex', sm: 'none' }, justifyContent: 'center', mb: 2 }}>
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={() => setActiveStep(activeStep - 1)}
                    variant="outlined"
                    size="small"
                    sx={{
                      borderRadius: 2,
                      borderColor: 'rgba(25, 118, 210, 0.3)',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'rgba(25, 118, 210, 0.1)',
                      },
                    }}
                  >
                    Back
                  </Button>
                </Box>
              )}

              {/* Step Content */}
            <Fade in timeout={500}>
              <Box>
                {/* Step 1: Username and Contact Verification */}
                {activeStep === 0 && (
                  <Box component="form" onSubmit={usernameFormik.handleSubmit}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Step 1: Verify Your Identity
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Enter your username and choose how you'd like to receive the verification code
                    </Typography>

                    <TextField
                      fullWidth
                      id="userName"
                      name="userName"
                      label="Username"
                      value={usernameFormik.values.userName}
                      onChange={usernameFormik.handleChange}
                      onBlur={usernameFormik.handleBlur}
                      error={usernameFormik.touched.userName && Boolean(usernameFormik.errors.userName)}
                      helperText={usernameFormik.touched.userName && usernameFormik.errors.userName}
                      margin="normal"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />

                    <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                      <FormLabel component="legend" sx={{ fontWeight: 600, mb: 2 }}>
                        How would you like to receive the verification code?
                      </FormLabel>
                      <RadioGroup
                        value={usernameFormik.values.contactMethod}
                        onChange={(e) => {
                          usernameFormik.setFieldValue('contactMethod', e.target.value);
                          usernameFormik.setFieldValue('contactValue', '');
                        }}
                      >
                        <FormControlLabel 
                          value="email" 
                          control={<Radio />} 
                          label="Email Address" 
                        />
                        <FormControlLabel 
                          value="phone" 
                          control={<Radio />} 
                          label="Phone Number" 
                        />
                      </RadioGroup>
                    </FormControl>

                    <TextField
                      fullWidth
                      id="contactValue"
                      name="contactValue"
                      label={usernameFormik.values.contactMethod === 'email' ? 'Email Address' : 'Phone Number'}
                      type={usernameFormik.values.contactMethod === 'email' ? 'email' : 'tel'}
                      value={usernameFormik.values.contactValue}
                      onChange={usernameFormik.handleChange}
                      onBlur={usernameFormik.handleBlur}
                      error={usernameFormik.touched.contactValue && Boolean(usernameFormik.errors.contactValue)}
                      helperText={usernameFormik.touched.contactValue && usernameFormik.errors.contactValue}
                      margin="normal"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {usernameFormik.values.contactMethod === 'email' ? 
                              <Email color="action" /> : 
                              <Phone color="action" />
                            }
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                      disabled={loading}
                      sx={{
                        py: 1.8,
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        borderRadius: 2,
                        background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                        boxShadow: '0 6px 20px rgba(25, 118, 210, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(25, 118, 210, 0.4)',
                        },
                      }}
                    >
                      {loading ? 'Sending OTP...' : 'Send Verification Code'}
                    </Button>
                  </Box>
                )}

                {/* Step 2: OTP Verification */}
                {activeStep === 1 && (
                  <Box component="form" onSubmit={otpFormik.handleSubmit}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Step 2: Enter Verification Code
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      We've sent a 6-digit verification code to your {usernameFormik.values.contactMethod}. 
                      Please enter it below.
                    </Typography>

                    <Card
                      sx={{
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        border: '1px solid rgba(25, 118, 210, 0.2)',
                        mb: 4,
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Security sx={{ color: 'primary.main', fontSize: 32, mb: 1 }} />
                        <Typography variant="body2" color="primary.main" fontWeight="medium">
                          Verification code sent to: {usernameFormik.values.contactValue}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Enter the 6-digit code to continue
                        </Typography>
                      </CardContent>
                    </Card>

                    {/* Enhanced OTP Input */}
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="subtitle1" sx={{ mb: 2, textAlign: 'center', fontWeight: 600 }}>
                        Verification Code
                      </Typography>
                      
                      {/* Individual OTP Digit Boxes */}
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          gap: { xs: 1, sm: 2 }, 
                          justifyContent: 'center',
                          mb: 2
                        }}
                      >
                        {Array.from({ length: 6 }, (_, index) => (
                          <TextField
                            key={index}
                            inputProps={{
                              maxLength: 1,
                              style: {
                                textAlign: 'center',
                                fontSize: '1.5rem',
                                fontWeight: 'bold',
                                color: '#1976d2',
                              }
                            }}
                            sx={{
                              width: { xs: '45px', sm: '55px' },
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: 'rgba(25, 118, 210, 0.03)',
                                '&:hover': {
                                  backgroundColor: 'rgba(25, 118, 210, 0.06)',
                                },
                                '&.Mui-focused': {
                                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'primary.main',
                                    borderWidth: 2,
                                    boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)',
                                  },
                                },
                              },
                            }}
                            value={otpFormik.values.otp[index] || ''}
                            onChange={(e) => {
                              const newOtp = otpFormik.values.otp.split('');
                              newOtp[index] = e.target.value;
                              const updatedOtp = newOtp.join('');
                              otpFormik.setFieldValue('otp', updatedOtp);
                              
                              // Auto focus next input
                              if (e.target.value && index < 5) {
                                const nextInput = e.target.parentElement?.parentElement?.nextElementSibling?.querySelector('input');
                                nextInput?.focus();
                              }
                            }}
                            onKeyDown={(e) => {
                              // Auto focus previous input on backspace
                              if (e.key === 'Backspace' && !e.target.value && index > 0) {
                                const prevInput = e.target.parentElement?.parentElement?.previousElementSibling?.querySelector('input');
                                prevInput?.focus();
                              }
                            }}
                            error={otpFormik.touched.otp && Boolean(otpFormik.errors.otp)}
                          />
                        ))}
                      </Box>

                      {/* Hidden input for formik validation */}
                      <TextField
                        sx={{ display: 'none' }}
                        id="otp"
                        name="otp"
                        value={otpFormik.values.otp}
                        onChange={otpFormik.handleChange}
                        onBlur={otpFormik.handleBlur}
                        error={otpFormik.touched.otp && Boolean(otpFormik.errors.otp)}
                        helperText={otpFormik.touched.otp && otpFormik.errors.otp}
                      />

                      {otpFormik.touched.otp && otpFormik.errors.otp && (
                        <Typography 
                          variant="caption" 
                          color="error" 
                          sx={{ display: 'block', textAlign: 'center', mt: 1 }}
                        >
                          {otpFormik.errors.otp}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        Didn't receive the code?
                      </Typography>
                      <Button
                        onClick={resendOtp}
                        disabled={resendTimer > 0 || loading}
                        sx={{ textTransform: 'none' }}
                      >
                        {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Code'}
                      </Button>
                    </Box>

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
                      disabled={loading}
                      sx={{
                        py: 1.8,
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        borderRadius: 2,
                        background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                        boxShadow: '0 6px 20px rgba(25, 118, 210, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(25, 118, 210, 0.4)',
                        },
                      }}
                    >
                      {loading ? 'Verifying...' : 'Verify Code'}
                    </Button>
                  </Box>
                )}

                {/* Step 3: Password Reset */}
                {activeStep === 2 && (
                  <Box component="form" onSubmit={passwordFormik.handleSubmit}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Step 3: Create New Password
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Choose a strong password that you haven't used before
                    </Typography>

                    <TextField
                      fullWidth
                      id="newPassword"
                      name="newPassword"
                      label="New Password"
                      type={showPassword ? 'text' : 'password'}
                      value={passwordFormik.values.newPassword}
                      onChange={passwordFormik.handleChange}
                      onBlur={passwordFormik.handleBlur}
                      error={passwordFormik.touched.newPassword && Boolean(passwordFormik.errors.newPassword)}
                      helperText={passwordFormik.touched.newPassword && passwordFormik.errors.newPassword}
                      margin="normal"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />

                    <TextField
                      fullWidth
                      id="confirmPassword"
                      name="confirmPassword"
                      label="Confirm New Password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={passwordFormik.values.confirmPassword}
                      onChange={passwordFormik.handleChange}
                      onBlur={passwordFormik.handleBlur}
                      error={passwordFormik.touched.confirmPassword && Boolean(passwordFormik.errors.confirmPassword)}
                      helperText={passwordFormik.touched.confirmPassword && passwordFormik.errors.confirmPassword}
                      margin="normal"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle confirm password visibility"
                              onClick={handleClickShowConfirmPassword}
                              edge="end"
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      startIcon={loading ? <CircularProgress size={20} /> : <LockReset />}
                      disabled={loading}
                      sx={{
                        py: 1.8,
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        borderRadius: 2,
                        background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                        boxShadow: '0 6px 20px rgba(25, 118, 210, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(25, 118, 210, 0.4)',
                        },
                      }}
                    >
                      {loading ? 'Resetting Password...' : 'Reset Password'}
                    </Button>
                  </Box>
                )}
              </Box>
            </Fade>

            {/* Security Trust Indicator */}
            <Box 
              sx={{ 
                mt: 4, 
                pt: 3, 
                borderTop: '1px solid rgba(25, 118, 210, 0.1)',
                textAlign: 'center' 
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <Security sx={{ color: 'success.main', fontSize: 18, mr: 1 }} />
                <Typography variant="caption" color="success.main" fontWeight="600">
                  Enterprise Security Protected
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                Your data is protected with bank-level encryption
              </Typography>
            </Box>

            {/* Footer */}
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Remember your password?{' '}
                <Link 
                  component={RouterLink} 
                  to="/auth/login" 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 'bold', 
                    textDecoration: 'none',
                    color: theme.palette.mode === 'dark' ? '#90caf9' : 'primary.main',
                    '&:hover': {
                      color: theme.palette.mode === 'dark' ? '#64b5f6' : 'primary.dark',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Sign in here
                </Link>
              </Typography>
            </Box>
              </Paper>
            </Box>
          </Slide>
        </Box>
      </Container>
    </Box>
  );
};

export default ForgotPassword;